import { useEffect, useMemo, useState } from "react";
import api, { formatApiError, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import ColumnFilter, { applyColumnFilters, dynamicUniques, SortableHeader, sortRows } from "@/components/ColumnFilter";
import { MagnifyingGlass, DownloadSimple, Signature, UploadSimple, Paperclip, PaperPlaneTilt } from "@phosphor-icons/react";
import SignatureModal from "@/components/SignatureModal";
import ScanUploadModal from "@/components/ScanUploadModal";
import { useAuth } from "@/context/AuthContext";

const EMPTY_FILTERS = {
  ddt_number: new Set(),
  created_at_day: new Set(),
  customer_name: new Set(),
  created_by_name: new Set(),
  quantity: new Set(),
  from_warehouse_name: new Set(),
};

export default function DDT() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [sort, setSort] = useState({ key: "created_at", dir: "desc" });
  const [downloadingId, setDownloadingId] = useState(null);
  const [resendingId, setResendingId] = useState(null);
  const [signModal, setSignModal] = useState(null); // { movementId, role }
  const [scanModal, setScanModal] = useState(null); // { movementId }

  const canResend = user?.role === "admin" || user?.role === "gestore";

  const resendEmail = async (r) => {
    setResendingId(r.id);
    try {
      const { data } = await api.post(`/movements/${r.id}/resend-ddt-email`);
      const n = data?.notification || {};
      if (n.sent) {
        const extra = [n.cc?.length ? ` · ${n.cc.length} cc` : "", n.bcc?.length ? ` · ${n.bcc.length} bcc` : ""].join("");
        toast.success(`Email DDT ${r.ddt_number} inviata a ${(n.to || []).join(", ")}${extra}`);
      } else if (n.skipped) {
        toast.warning(`Invio saltato: ${n.skipped}`);
      } else {
        toast.error(n.error || "Invio fallito");
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setResendingId(null); }
  };

  const load = async () => {
    try {
      const [ex, rs] = await Promise.all([
        api.get("/movements", { params: { type: "exit", limit: 2000 } }),
        api.get("/movements", { params: { type: "reso_fornitore", limit: 2000 } }),
      ]);
      setRows([...ex.data, ...rs.data].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || "")));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { load(); }, []);

  const downloadPdf = async (m) => {
    setDownloadingId(m.id);
    try {
      const t = localStorage.getItem("token");
      const res = await fetch(`${API_BASE}/movements/${m.id}/pdf`, {
        credentials: "include",
        headers: t ? { Authorization: `Bearer ${t}` } : {},
      });
      if (!res.ok) throw new Error(await res.text());
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      const slug = (m.ddt_number || `DDT-${m.id.slice(0, 8)}`).replace(/\//g, "-").replace(/\s/g, "");
      a.href = url; a.download = `${slug}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e) || "Errore download DDT"); }
    finally { setDownloadingId(null); }
  };

  const setColFilter = (key) => (v) => setFilters((f) => ({ ...f, [key]: v }));
  const resetFilters = () => { setFilters(EMPTY_FILTERS); setQ(""); setSort({ key: "created_at", dir: "desc" }); };

  // ---- Accorpamento: raggruppa i movimenti dello stesso DDT in un'unica riga ----
  // Ogni riga aggregata riporta il totale delle quantità di tutti i prodotti
  // del gruppo. Le firme e la scansione sono condivise (mostro quelle del
  // sibling con il group_index più basso). Legacy: se manca ddt_number la riga
  // resta singola.
  const aggregatedRows = useMemo(() => {
    const byDdt = new Map();
    const ungrouped = [];
    for (const r of rows) {
      const key = r.ddt_number;
      if (!key) { ungrouped.push({ ...r, group_size: 1, group_items: [r] }); continue; }
      if (!byDdt.has(key)) byDdt.set(key, []);
      byDdt.get(key).push(r);
    }
    const merged = [];
    for (const [ddt, items] of byDdt.entries()) { // eslint-disable-line no-unused-vars
      items.sort((a, b) => (a.group_index ?? 0) - (b.group_index ?? 0));
      const lead = items[0];
      const totalQty = items.reduce((s, x) => s + (Number(x.quantity) || 0), 0);
      merged.push({
        ...lead,
        quantity: totalQty,
        group_size: items.length,
        group_items: items,
      });
    }
    return [...merged, ...ungrouped];
  }, [rows]);

  const accessors = useMemo(() => ({
    ddt_number: (r) => r.ddt_number || "",
    created_at: (r) => r.created_at || "",
    created_at_day: (r) => (r.created_at || "").slice(0, 10),
    customer_name: (r) => r.customer_name || (r.supplier_dest_name ? `Reso: ${r.supplier_dest_name}` : ""),
    created_by_name: (r) => r.created_by_name || r.created_by || "",
    quantity: (r) => Number(r.quantity || 0),
    from_warehouse_name: (r) => r.from_warehouse_name || "",
  }), []);

  const uniques = useMemo(
    () => dynamicUniques(aggregatedRows, filters, accessors),
    [aggregatedRows, filters, accessors]
  );

  const filteredRows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const base = !needle ? aggregatedRows : aggregatedRows.filter((r) => {
      const s = [r.ddt_number, r.customer_name, r.from_warehouse_name, r.created_by_name, r.created_by]
        .filter(Boolean).join(" ").toLowerCase();
      return s.includes(needle);
    });
    return sortRows(applyColumnFilters(base, filters, accessors), sort, accessors);
  }, [aggregatedRows, q, filters, sort, accessors]);

  const hasActiveFilters = q || Object.values(filters).some((s) => s.size > 0);

  return (
    <div>
      <PageHeader
        title="DDT"
        subtitle="Documenti di trasporto emessi: uscite verso clienti e resi a fornitore"
      />
      <div className="p-4 md:p-8 space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 bg-white border border-neutral-200 px-3 py-2 flex-1 min-w-[200px] max-w-md">
            <MagnifyingGlass size={16} className="text-neutral-400" />
            <input
              data-testid="ddt-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Cerca N° DDT, cliente, operatore, magazzino..."
              className="flex-1 outline-none text-sm bg-transparent"
            />
          </div>
          {hasActiveFilters && (
            <button data-testid="ddt-reset-filters" onClick={resetFilters} className="px-3 py-2 border border-neutral-300 text-xs uppercase tracking-wider hover:bg-neutral-50">
              Reset filtri
            </button>
          )}
          <div className="text-xs text-neutral-500 ml-auto" data-testid="ddt-row-count">
            {filteredRows.length} {filteredRows.length === 1 ? "DDT" : "DDT"} {hasActiveFilters ? "(filtrati)" : ""}
          </div>
        </div>

        <div className="bg-white border border-neutral-200 overflow-auto max-h-[calc(100vh-260px)]" data-testid="ddt-table">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 border-b border-neutral-200 sticky top-0 z-20 shadow-sm">
              <tr>
                <th className="px-4 py-3 label-eyebrow bg-neutral-50">
                  <span className="inline-flex items-center gap-1">
                    <SortableHeader testid="sort-ddt-num" label="N° DDT" sortKey="ddt_number" sortConfig={sort} setSortConfig={setSort} />
                    <ColumnFilter testid="filter-ddt-num" values={uniques.ddt_number} selected={filters.ddt_number} onChange={setColFilter("ddt_number")} />
                  </span>
                </th>
                <th className="px-4 py-3 label-eyebrow bg-neutral-50">
                  <span className="inline-flex items-center gap-1">
                    <SortableHeader testid="sort-ddt-date" label="Data" sortKey="created_at" sortConfig={sort} setSortConfig={setSort} />
                    <ColumnFilter testid="filter-ddt-date" values={uniques.created_at_day} selected={filters.created_at_day} onChange={setColFilter("created_at_day")} />
                  </span>
                </th>
                <th className="px-4 py-3 label-eyebrow bg-neutral-50">
                  <span className="inline-flex items-center gap-1">
                    <SortableHeader testid="sort-ddt-customer" label="Cliente / Fornitore" sortKey="customer_name" sortConfig={sort} setSortConfig={setSort} />
                    <ColumnFilter testid="filter-ddt-customer" values={uniques.customer_name} selected={filters.customer_name} onChange={setColFilter("customer_name")} />
                  </span>
                </th>
                <th className="px-4 py-3 label-eyebrow bg-neutral-50">
                  <span className="inline-flex items-center gap-1">
                    <SortableHeader testid="sort-ddt-operator" label="Operatore" sortKey="created_by_name" sortConfig={sort} setSortConfig={setSort} />
                    <ColumnFilter testid="filter-ddt-operator" values={uniques.created_by_name} selected={filters.created_by_name} onChange={setColFilter("created_by_name")} />
                  </span>
                </th>
                <th className="px-4 py-3 label-eyebrow bg-neutral-50">
                  <span className="inline-flex items-center gap-1">
                    <SortableHeader testid="sort-ddt-from" label="Magazzino" sortKey="from_warehouse_name" sortConfig={sort} setSortConfig={setSort} />
                    <ColumnFilter testid="filter-ddt-from" values={uniques.from_warehouse_name} selected={filters.from_warehouse_name} onChange={setColFilter("from_warehouse_name")} />
                  </span>
                </th>
                <th className="px-4 py-3 label-eyebrow text-right bg-neutral-50">
                  <span className="inline-flex items-center gap-1 justify-end w-full">
                    <SortableHeader testid="sort-ddt-qty" label="Qtà" sortKey="quantity" sortConfig={sort} setSortConfig={setSort} align="right" />
                    <ColumnFilter testid="filter-ddt-qty" values={uniques.quantity} selected={filters.quantity} onChange={setColFilter("quantity")} align="right" />
                  </span>
                </th>
                <th className="px-4 py-3 label-eyebrow text-right bg-neutral-50">PDF</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.length === 0 ? (
                <tr><td colSpan="6" className="px-4 py-12 text-center text-neutral-500">Nessun DDT trovato con i filtri correnti.</td></tr>
              ) : filteredRows.map((r) => {
                const isSigned = !!r.signatures?.destinatario;
                return (
                <tr key={r.id} data-testid={`ddt-row-${r.id}`} className="border-b border-neutral-100 hover:bg-[#002FA7]/5 transition-colors">
                  <td className="px-4 py-3 font-mono text-xs font-semibold whitespace-nowrap">
                    <span className="inline-flex items-center gap-1.5">
                      {r.ddt_number || "—"}
                      {r.group_size > 1 && (
                        <span
                          data-testid={`ddt-group-badge-${r.id}`}
                          title={`${r.group_size} prodotti raggruppati in questo DDT`}
                          className="px-1.5 py-0.5 text-[9px] uppercase tracking-wider border border-[#002FA7] text-[#002FA7] bg-[#002FA7]/5 font-sans"
                        >
                          × {r.group_size}
                        </span>
                      )}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-neutral-600 whitespace-nowrap">{new Date(r.created_at).toLocaleString("it-IT")}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {r.type === "reso_fornitore" ? (
                      <span data-testid={`ddt-reso-${r.id}`}><span className="mr-1.5 px-1.5 py-0.5 text-[10px] uppercase tracking-wider bg-orange-100 text-orange-800">Reso a fornitore</span>{r.supplier_dest_name || "—"}</span>
                    ) : (r.customer_name || "—")}
                  </td>
                  <td className="px-4 py-3 text-xs text-neutral-700 whitespace-nowrap" title={r.created_by}>{r.created_by_name || r.created_by || "—"}</td>
                  <td className="px-4 py-3 text-xs whitespace-nowrap">{r.from_warehouse_name || "—"}</td>
                  <td className="px-4 py-3 text-right font-mono font-semibold whitespace-nowrap">{r.quantity}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-1.5 flex-wrap justify-end">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-wider border ${
                        r.signatures?.incaricato ? "border-emerald-500 text-emerald-700 bg-emerald-50" : "border-neutral-300 text-neutral-500"
                      }`} title={r.signatures?.incaricato ? "Incaricato firmato" : "Incaricato da firmare"}>
                        <Signature size={11} weight="bold" />
                        Inc. {r.signatures?.incaricato ? "✓" : "—"}
                      </span>
                      <span className={`inline-flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-wider border ${
                        r.signatures?.destinatario ? "border-emerald-500 text-emerald-700 bg-emerald-50" : "border-amber-500 text-amber-700 bg-amber-50"
                      }`} title={r.signatures?.destinatario ? "Destinatario firmato" : "Destinatario da firmare"}>
                        <Signature size={11} weight="bold" />
                        Dest. {r.signatures?.destinatario ? "✓" : "—"}
                      </span>
                      {!isSigned && (
                        <>
                          <button
                            data-testid={`ddt-sign-${r.id}`}
                            onClick={() => setSignModal({ movementId: r.id, role: "destinatario" })}
                            title="Acquisisci firma"
                            className="p-1.5 border border-[#002FA7] text-[#002FA7] hover:bg-[#002FA7] hover:text-white transition-colors"
                          >
                            <Signature size={13} weight="bold" />
                          </button>
                          <button
                            data-testid={`ddt-upload-scan-${r.id}`}
                            onClick={() => setScanModal({ movementId: r.id })}
                            title="Carica scansione firmata"
                            className="p-1.5 border border-neutral-400 text-neutral-600 hover:bg-neutral-100 transition-colors"
                          >
                            <UploadSimple size={13} weight="bold" />
                          </button>
                        </>
                      )}
                      {r.signed_scan && (
                        <span
                          data-testid={`ddt-scan-badge-${r.id}`}
                          title={`Scansione firmata caricata${r.signed_scan.uploaded_at ? " il " + new Date(r.signed_scan.uploaded_at).toLocaleString("it-IT") : ""}`}
                          className="inline-flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-wider border border-emerald-500 text-emerald-700 bg-emerald-50"
                        >
                          <Paperclip size={11} weight="bold" />
                          Scansione
                        </span>
                      )}
                      <button
                        data-testid={`ddt-pdf-${r.id}`}
                        onClick={() => downloadPdf(r)}
                        disabled={downloadingId === r.id}
                        className="inline-flex items-center gap-1 px-2 py-1.5 border border-[#002FA7] text-[#002FA7] hover:bg-[#002FA7] hover:text-white text-xs uppercase tracking-wider transition-colors disabled:opacity-50"
                      >
                        <DownloadSimple size={13} weight="bold" />
                        {downloadingId === r.id ? "..." : "PDF"}
                      </button>
                      {canResend && r.type === "exit" && (
                        <button
                          data-testid={`ddt-resend-email-${r.id}`}
                          onClick={() => resendEmail(r)}
                          disabled={resendingId === r.id}
                          title="Reinvia email DDT emesso al cliente (template associato)"
                          className="inline-flex items-center gap-1 px-2 py-1.5 border border-[#002FA7] text-[#002FA7] hover:bg-[#002FA7] hover:text-white text-xs uppercase tracking-wider transition-colors disabled:opacity-50"
                        >
                          <PaperPlaneTilt size={13} weight="bold" />
                          {resendingId === r.id ? "..." : "Email"}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <SignatureModal
        open={!!signModal}
        onClose={() => setSignModal(null)}
        movementId={signModal?.movementId}
        role={signModal?.role}
        movementType={rows.find((r) => r.id === signModal?.movementId)?.type || "exit"}
        ddtNumber={rows.find((r) => r.id === signModal?.movementId)?.ddt_number}
        onSigned={async () => await load()}
      />
      <ScanUploadModal
        open={!!scanModal}
        onClose={() => setScanModal(null)}
        movementId={scanModal?.movementId}
        movementType="exit"
        onUploaded={() => load()}
      />
    </div>
  );
}
